/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import {
    type BaseRequestOptionsArgs,
    type Filter,
    type HttpResponse,
    type Rest,
    Consts,
    CORE_CONFIG,
    ExtendService,
    HttpService,
    I18nService,
    PermissionService,
    TimeService,
    UtilService,
} from '@dm/core';
import { FCPORT_RES_BATCH, MO } from 'app/common/constants';
import { ToolService } from 'app/common/services';
import { type Dtree, type FileSystem, type LifPort, type PmsStatus } from 'app/common/types/mo';
import { FeatureConfig } from 'app/config/feature';
import { SWITCH } from 'app/config/switch';
import { RepType } from 'app/feature/service/file-service/file-system/file-system-str.service';
import { type QueryQuotaTypeE } from 'app/feature/service/file-service/quota/user-quota/user-quota-str.service';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { GlobalSearchService } from '../global-search.service';
import { DataTypeE, GroupTypeE } from '../global-search-str.service';
import { type SearchParams, DEFAULT_FILTERS, DEFAULT_STRING_VALUE, TIME_SEGMENTS } from './const';
import { mapType, PRECISION_TYPE } from './grammar/gammar';
import { transformType } from './utils';

@Injectable({
    providedIn: 'root',
})
export class InfigoService {
    language = this.i18nService.getLanguage();

    constructor(
        private httpService: HttpService,
        private ext: ExtendService,
        protected timeService: TimeService,
        private i18nService: I18nService,
        public globalSearchService: GlobalSearchService,
        private utilService: UtilService,
        public permissionService: PermissionService,
        public i18n: I18nService,
        public toolService: ToolService,
    ) {
    }

    transformSearchParams(searchItem: { [key: string]: any }, defaultType?: string): SearchParams {
        const precisionType = searchItem.precisionType ? searchItem.precisionType : defaultType;
        const license = this.globalSearchService.dataMap[precisionType].page.license as string[] | string[][];
        const resultItem = {
            searchKey: searchItem.name || searchItem.NAME,
            name: searchItem.name || searchItem.NAME,
            url: this.globalSearchService.dataMap[precisionType].url,
            type: precisionType,
            groupType: GroupTypeE.object,
            isObject: true,
            objectDes: this.i18n.get(this.globalSearchService.dataMap[precisionType].objectDes),
            action: 'searchDetail', // 约定方法名叫searchDetail 不可重名
            actionParams: searchItem.id || searchItem.ID,
            tab: this.globalSearchService.dataMap[precisionType].tab,
            subTab: this.globalSearchService.dataMap[precisionType].subTab,
            show:
                this.permissionService.checkShow(this.globalSearchService.dataMap[precisionType].page.permission, license) &&
                (!this.globalSearchService.dataMap[precisionType].isNASFeature || SWITCH.SUPPORT_NAS),
        };
        return resultItem;
    }

    /**
     * 查询lun
     * @param id
     * @param options
     */
    getLunDetail(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('lun.get').format(id);
        return this.httpService.get(rest, options);
    }

    /**
     * 查询lun - 只获取 ID NAME USAGETYPE
     * @param id
     * @param options
     */
    getLunSubType(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('lun.get').format(id).setParams({ selectFields: 'ID,NAME,SUBTYPE' });
        return this.httpService.get(rest, options);
    }

    getRecycleLunDetail(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('lun.associate');
        rest.setParams({
            ASSOCIATEOBJTYPE: 57880,
            ASSOCIATEOBJID: id,
        });
        return this.httpService.get(rest, options);
    }

    // 查询单个LUN组
    getLUNGroupDetail(id: number | string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('lunGroup.get').format(id);
        return this.httpService.get(rest, options);
    }

    /**
     * 查询主机
     * @param hostId
     */
    getHost(hostId: string): Observable<HttpResponse> {
        const dataRest = this.httpService.makeUrl('host.get').format(hostId);
        return this.httpService.get(dataRest);
    }

    /**
     * 查询主机组详情
     * @param hostGroupId 主机组ID
     */
    getHostGroup(hostGroupId: string): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('hostGroup.get').format(hostGroupId);
        return this.httpService.get(rest);
    }

    /**
     * 查询多租户信息
     * @param id
     */
    getVstore(id: string): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('vstore.get').format(id);
        return this.httpService.get(rest);
    }

    /**
     * 判断是否双活租户
     * @param id
     */
    getVstorePair(id: string): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('vstoreHyperMetroPair.batch').setFilters([
            { field: 'LOCALVSTOREID', matchMode: '::', value: id },
        ]).setParams(
            { REPTYPE: RepType.HyperMetro },
        );
        return this.httpService.get(rest);
    }

    /**
     * 查询端口组
     * @param id
     */
    getPortGroup(id: string): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('portGroup.get').format(id);
        return this.httpService.get(rest);
    }

    /**
     * 查询文件系统详细信息
     * @param id
     */
    getFileSystem(id: string): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('fileSystem.get').format(id);
        return this.httpService.get(rest);
    }

    /**
     * 查询dtree列表
     * @param parentId
     */
    getDtrees(parentId: string, shareType?: string, vstoreId?: string): Observable<HttpResponse> {
        const params = {
            PARENTID: parentId,
            shareType,
            vstoreId,
        };
        if (!shareType) {
            delete params.shareType;
        }
        const rest = this.httpService.makeUrl('dtree.getBatch').setParams(params);
        return this.httpService.get(rest);
    }

    /**
     * 批量查询配额
     * @param parentType
     * @param parentId
     * @param queryType 0：所有，1：用户配置，2：配额报告
     */
    getQuotaList(parentType: number, parentId: string, queryType?: QueryQuotaTypeE, vstoreId?: string): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('quota.getBatch');
        rest.setParams({
            PARENTTYPE: parentType,
            PARENTID: parentId,
            QUERYTYPE: queryType,
            SPACEUNITTYPE: 1, // 指定容量单位为KB
            range: '[0-100]',
        });
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.httpService.get(rest);
    }

    /**
     * 查询NFS / CIFS
     * @param url
     * @param fs
     * @param dtree
     */
    getNFSOrCIFSShareData(url: string, fs: FileSystem, dtree?: Dtree): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl(url, undefined, true);
        rest.setParams({ vstoreId: fs && fs.vstoreId })
            .setFilters(dtree ? [
                { field: 'FSID', matchMode: '::', value: fs && fs.ID },
                { field: 'DTREEID', matchMode: '::', value: dtree && dtree.ID },
            ] : [{ field: 'FSID', matchMode: '::', value: fs && fs.ID }]);
        rest.setTempParams({
            range: `[0-100]`,
            sortby: '',
        });
        return this.httpService.get(rest);
    }

    /**
     * 查询逻辑端口
     * @param id
     */
    getLogicPort(id: string): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('lif.get').format(id);
        return this.httpService.get(rest);
    }

    /**
     * 查询指定快照信息
     * @param id
     */
    getSpecialSnapShot(id: string): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('snapshot.get').format(id);
        return this.httpService.get(rest);
    }

    /**
     * 计算容量利用率( LUN/LUNGROUP HOST/HOSTGROUP 保持一致 )
     * @param data 包含容量相关字段的数据
     */
    usageValue(data: {
        CAPACITY: string | number; CAPCITY: string | number; capacity: string | number; fileSystemCapacity: number;
        ALLOCCAPACITY: string | number; allocatedCapacity: string | number; fileSystemUsedCapacity: number;
        allocatedPoolQuota: string | number;
    }): number {
        const capacity = Number(data.CAPACITY) || Number(data.CAPCITY) || Number(data.capacity) || data.fileSystemCapacity || 0;
        const allocatedCapacity = Number(data.ALLOCCAPACITY) || Number(data.allocatedCapacity) ||
            data.fileSystemUsedCapacity || Number(data.allocatedPoolQuota) || 0;
        return capacity && (allocatedCapacity / capacity * 100);
    }

    getHistoryPerformance(id: string, type: string, indicators: string[], segment: string): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('performanceData.getBatch', 'V2');
        const currentTime = this.timeService.currTime * 1000;
        const params = {
            begin_time: currentTime - TIME_SEGMENTS[segment] * 1000,
            end_time: currentTime,
            objects: [
                {
                    ids: [
                        `${id}`,
                    ],
                    indicators,
                    object_type: MO[type],
                },
            ],
            compute_mode: 'avg',
        };
        const api = {
            rest,
            method: 'POST',
            postBody: params,
        };
        return this.httpService.post(rest, api.postBody);
    }

    getSourceBatch = (api: { url: string; filters: { field: any }[]; type: string },
        range: string): Observable<SearchParams | SearchParams[]> => {
        if (api.url === 'performanceData.getTopBatch') {
            const restAPI = this.httpService.makeUrl(api.url, 'V2');
            const {
                TIME_SEGMENT,
                compute_mode,
                indicators,
                limit,
                object_type,
                sort_indicator,
                sort_type,
            } = api.filters[0] as any; // 默认只有一个filters
            const end_time = this.timeService.currTime * 1000;
            const begin_time = end_time - TIME_SEGMENTS[TIME_SEGMENT] * 1000;
            const body = {
                compute_mode,
                indicators,
                limit,
                object_type: MO[transformType(object_type)],
                sort_indicator,
                sort_type: sort_type.replace(/\d/g, ''),
                begin_time,
                end_time,
            };
            return this.httpService.post(restAPI, body).pipe(
                map((value) => value ?
                    this.globalSearchService.transform(value.data || [], null, PRECISION_TYPE[api.type]) :
                    []),
            );
        }
        const rest = this.httpService.makeUrl(api.url).setFilters([
            ...DEFAULT_FILTERS[api.type],
            ...api.filters,
        ]);
        rest.setParams(api.filters[0] ? {
            sortby: `${api.filters[0].field},a`,
            range: range || '[0-21]',
        } : {
            range: range || '[0-21]',
        });
        if (api.type === 'LOGICALPORTS') {
            rest.setParams({
                vStoreId: -1,
                timeConversion: 0,
            });
        }
        if (api.type === 'FILESYSTEMS') {
            rest.setParams({ vStoreId: -1 });
        }
        if (api.type === 'LUNSNAPSHOTS') {
            rest.setParams({ timeConversion: 0 });
        }
        return this.httpService.get(rest).pipe(
            map((value) => value ? this.globalSearchService.transform(value.data || [], null, PRECISION_TYPE[api.type]) : []),
        );
    };

    checkPmsLunStatus(opt?: BaseRequestOptionsArgs): Observable<PmsStatus> {
        // 微存储屏蔽性能监控，不查询pms
        if (FeatureConfig.IsHidePms) {
            return of(null);
        }
        const rest = this.httpService.makeUrl('pmsLun.checkStatus', 'V2');
        return this.httpService.get(rest, opt).pipe(
            map(({ data }) => data),
        );
    }

    // 查询LUN组中的LUN
    getAssociateLun(groupId: string | number): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('lun.associate');
        rest.setParams({
            TYPE: MO.LUN,
            ASSOCIATEOBJTYPE: MO.LUN_GROUP,
            ASSOCIATEOBJID: groupId,
        });
        return this.httpService.get(rest);
    }

    /**
     * 查询定主机组所关联的主机
     * @param groupId 主机组ID
     */
    getAssociateHosts(groupId: string): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('host.getAssociate');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.HOST_GROUP_TYPE,
            ASSOCIATEOBJID: groupId,
        });

        return this.httpService.get(rest);
    }

    /**
     * 查询端口组中的所有物理端口
     * @param portGroupId 端口组ID
     */
    getAssociatePorts(portGroupId: string): Observable<any[]> {
        const fcPortRest = this.httpService.makeUrl('port.getFCPortAssociate')
            .setParams({
                ASSOCIATEOBJTYPE: MO.PORT_GROUP_TYPE,
                ASSOCIATEOBJID: portGroupId,
            });
        const ethPortRest = this.httpService.makeUrl('port.getETHPortAssociate')
            .setParams({
                ASSOCIATEOBJTYPE: MO.PORT_GROUP_TYPE,
                ASSOCIATEOBJID: portGroupId,
            });
        const fcPortRes$ = !FeatureConfig.IsSupportFC ? of(FCPORT_RES_BATCH) : this.httpService.get(fcPortRest);
        const ethPortRes$ = this.httpService.get(ethPortRest);
        return this.ext.forkAll([fcPortRes$, ethPortRes$]).pipe(
            map(([fcPort, ethPort]) => [...(fcPort.data || []), ...(ethPort.data || [])]),
        );
    }

    /**
     * 查询iSCSL和Roce的LIF端口
     */
    getPortGroupAssociateLif(portGroupId: string): Observable<LifPort[]> {
        const rest = this.httpService.makeUrl('lif.associate').setParams({
            ASSOCIATEOBJTYPE: MO.PORT_GROUP_TYPE,
            ASSOCIATEOBJID: portGroupId,
        });
        return this.httpService.get(rest).pipe(
            map(({ data }: { data: LifPort[] }) => data),
        );
    }

    /**
     * 查询关联的映射
     * @param type
     * @param id
     * @param groupFilters
     */
    getAssociateMappings(type: string, id: string, groupFilters?: Filter[][]): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('mapping.mappedObjects').setParams({
            ASSOCIATEOBJTYPE: MO[type],
            ASSOCIATEOBJID: id,
        });
        if (groupFilters) {
            rest.setGropFilters(groupFilters);
        }
        return this.httpService.get(rest);
    }

    /**
     * 查询指定主机所关联的启动器
     * @param hostId 主机ID
     */
    getHostInitiators(hostId: string, opt?: BaseRequestOptionsArgs): Observable<any[]> {
        const fcRest = this.httpService.makeUrl('fcInitiator.getBatch').setParams({ PARENTID: hostId });
        const iscsiRest = this.httpService.makeUrl('iscsiInitiator.getBatch').setParams({ PARENTID: hostId });
        const nvmeofRoceRest = this.httpService.makeUrl('nvmeofRoceInitiator.getBatch').setParams({ PARENTID: hostId });

        // 微存储直通屏蔽fc
        const fc$ = SWITCH.IS_DIRECT_MODE ? of(null) : this.httpService.get(fcRest, opt);

        // 微存储直通屏蔽iscsi
        const iscsi$ = SWITCH.IS_DIRECT_MODE ? of(null) : this.httpService.get(iscsiRest, opt);
        // 入门级存储屏蔽NvmeofRoce启动器
        const nvmeofRoce$ = !FeatureConfig.IsSupportNVMEOverRoCEInitiator ? of(null) : this.httpService.get(nvmeofRoceRest, opt);

        return this.ext.forkAll([fc$, iscsi$, nvmeofRoce$]).pipe(
            map(([fcRes, iscsiRes, nvmeofRoceRes]) => [...(fcRes && fcRes.data || []), ...(iscsiRes && iscsiRes.data || []),
            ...(nvmeofRoceRes && nvmeofRoceRes.data || [])]),
        );
    }

    /**
     * 查询主机LUN ID
     * @param {*} param 参数对象，包含5种字段组合下发场景：
     *                   1：主机ID
     *                   2：主机组ID
     *                   3：主机ID和LUNID
     *                   4：主机ID和LUNGroupId
     *                   5：主机组ID和LUNGroupId
     * @param {{ [key: string]: any }} [filter] 过滤参数对象
     * @memberof MappingService
     */
    getHostLun(param: { [key: string]: any }, filter?: Filter[]): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('mapping.hostLunInfo');
        rest.setParams(param);
        if (filter) {
            rest.setFilters(filter);
        }
        return this.httpService.get(rest);
    }

    /**
     * 获取当前告警信息
     * alarmObjType - 告警对象
     * @param value
     * @param range
     */
    getCurrentAlarm = (value: string, range?: string): Observable<any> => {
        const typeId = this.getTypeIdFrom(value);
        if (!typeId) {
            return new Observable<any>((o) => o.next([]));
        }

        const restMake = (url: string): Rest => this.httpService.makeUrl(url).setFilters([{
            field: 'alarmObjType',
            matchMode: '::',
            value: typeId,
        }]);
        const rest = restMake('alarm.getCurrentAlarm');
        rest.setParams({
            range: range || '[0-21]',
            timeConversion: 0,
        });
        const $ob1 = this.httpService.get(rest).pipe(
            map(({ data }) => this.globalSearchService.transform(data, null, DataTypeE.alarm)),
        );
        const rest2 = restMake('alarm.getAlarmCount');
        const $ob2 = this.httpService.get(rest2).pipe(map(({ data }) => data.COUNT));
        return forkJoin([$ob1, $ob2]).pipe(map(([data, count]) => {
            (data as any).count = count;
            return data;
        }));
    };

    getTypeIdFrom(str: string): string {
        let type = str.split(' ')[0];
        type = mapType(type);
        type = transformType(type);
        return MO[type];
    }

    /**
     * 获取所有事件信息
     * alarmObjType - 告警对象
     * @param value
     * @param range
     */
    getAllEvent(value: string, range?: string): Observable<any> {
        const typeId = this.getTypeIdFrom(value);

        if (!typeId) {
            return new Observable<any>((o) => o.next([]));
        }

        const restMake = (url: string): Rest => this.httpService.makeUrl(url).setFilters([{
            field: 'alarmObjType',
            matchMode: '::',
            value: typeId,
        }]);
        const rest = restMake('alarm.getAllEvent');
        rest.setParams({
            range: range || '[0-21]',
            timeConversion: 0,
        });
        const $ob1 = this.httpService.get(rest).pipe(
            map(({ data }) => this.globalSearchService.transform(data, null, DataTypeE.event)),
        );
        const rest2 = restMake('alarm.getAllEventCount');
        const $ob2 = this.httpService.get(rest2).pipe(map(({ data }) => data.COUNT));
        return forkJoin([$ob1, $ob2]).pipe(map(([data, count]) => {
            (data as any).count = count;
            return data;
        }));
    }

    /**
     * 获取LUN关联的LUN组
     * @param lunId
     * @param options
     */
    getAssociateLunGroup(lunId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('lunGroup.associate');
        rest.setParams({
            TYPE: MO.LUN_GROUP,
            ASSOCIATEOBJTYPE: MO.LUN,
            ASSOCIATEOBJID: lunId,
        });
        return this.httpService.get(rest, options);
    }

    /**
     * 跳转到帮助页面
     */
    getHelpDocUrl(): string {
        let url: string;
        const helpMap = CORE_CONFIG.helpMap;
        if (helpMap) {
            const location = window.location;
            const baseUrl = `${location.protocol}//${location.host}`;
            const language = this.language;
            url = `${baseUrl + helpMap.rootPath}/help_${language}/Online_Help.html`;
        }
        return url;
    }

    /**
     * 判断是否为容量单位
     * @param unit
     */
    isCapacityUnit(unit: string): boolean {
        const reg = [
            Consts.BYTE, Consts.KB, Consts.MB, Consts.GB, Consts.TB, Consts.PB,
            Consts.EB, Consts.ZB, Consts.YB, Consts.BB, Consts.NB, Consts.DB,
        ].join('|');
        return new RegExp('\\b(' + reg + ')\\b', 'i').test(unit);
    }

    /**
     * 自适应显示值
     * @param value
     * @param unit
     * @param num
     */
    adaptValue(value: number, unit: string, num = 0): Array<any> {
        if (Number.isNaN(value)) {
            return [DEFAULT_STRING_VALUE, ''];
        }

        if (unit === 'us') {
            return [(value / 1000).toFixed(num), 'ms'];
        }
        if (this.isCapacityUnit(unit)) {
            // 将MB/s单位分割为MB进行转换
            const tempUnit = unit.split('/');
            if (value === 0) {
                return [value.toFixed(num), `${tempUnit[0]}${tempUnit[1] ? '/' + tempUnit[1] : ''}`];
            }
            const res = this.utilService.adaptUnit(value, tempUnit[0]);
            return [res.value.toFixed(num), `${res.unit}${tempUnit[1] ? '/' + tempUnit[1] : ''}`];
        }
        return [value.toFixed(num), unit];
    }
}
